import {
  CalendarIcon,
  ChatIcon,
  CheckListIcon,
  CirclesIcon,
  HomeIcon,
  IconSize,
  MembersIcon,
} from "@shared/components/ui/Icon";
import { UserRole } from "../../../features/settings/types/settings.types";
import { SidebarConfig, SidebarItemType } from "./types";

const StartHereIcon = ({ active: _active }: { active: boolean }) => (
  <svg
    className="shrink-0 fill-current"
    style={{ color: '#ff8a3b' }}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
  >
    <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.5 8.5-5 3A.5.5 0 0 1 6 11V5a.5.5 0 0 1 .757-.429l5 3a.5.5 0 0 1 0 .858z" />
  </svg>
);

const TasksIcon = ({ active }: { active: boolean }) => (
  <svg
    className={`shrink-0 fill-current ${active ? "text-blue-500" : "text-gray-400 dark:text-gray-500"}`}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
  >
    <path d="M7.586 9H1a1 1 0 1 1 0-2h6.586L6.293 5.707a1 1 0 0 1 1.414-1.414l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 1 1-1.414-1.414L7.586 9ZM3.075 4.572a1 1 0 1 1-1.64-1.144 8 8 0 1 1 0 9.144 1 1 0 0 1 1.64-1.144 6 6 0 1 0 0-6.856Z" />
  </svg>
);

const MessagesIcon = ({ active }: { active: boolean }) => (
  <svg
    className={`shrink-0 ${active ? "text-blue-500" : "text-gray-400 dark:text-gray-500"}`}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
  >
    <g strokeWidth="2">
      <path
        d="M12.337,4.767c-1.095-1.806-3.074-3.017-5.34-3.017C3.547,1.75,.75,4.547,.75,7.998c0,1.136,.308,2.199,.839,3.117,.37,.695-.045,2.337-.839,3.13,1.077,.058,2.497-.428,3.13-.839,.421,.243,1.09,.566,1.964,.731,.112,.021,.232,.017,.346,.032"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.75,7.246c2.485,0,4.5,2.015,4.5,4.5,0,.819-.222,1.584-.604,2.246-.267,.5,.033,1.683,.604,2.255-.776,.042-1.798-.309-2.255-.604-.303,.175-.785,.407-1.415,.527-.269,.051-.547,.078-.831,.078-2.486,0-4.5-2.015-4.5-4.5,0-2.486,2.015-4.5,4.5-4.5Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const ConnectionsIcon = ({ active }: { active: boolean }) => (
  <svg
    className={`shrink-0 ${active ? "text-blue-500" : "text-gray-400 dark:text-gray-500"}`}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="6" cy="5" r="2.5" />
    <path d="M1 14.5c0-2.485 2.239-4.5 5-4.5s5 2.015 5 4.5" />
    <circle cx="12.5" cy="4.5" r="2" />
    <path d="M11 9.5c1.105-.35 2.5-.15 3.5.5" />
  </svg>
);

const OrganizationCommunityIcon = ({ active }: { active: boolean }) => (
  <svg
    className={`shrink-0 ${active ? "text-blue-500" : "text-gray-400 dark:text-gray-500"}`}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="3" width="14" height="11" rx="1.5" />
    <circle cx="8" cy="7" r="2" />
    <path d="M4 14c0-2.21 1.79-4 4-4s4 1.79 4 4" />
  </svg>
);

const SupportIcon = ({ active }: { active: boolean }) => (
  <svg
    className={`shrink-0 ${active ? "text-blue-500" : "text-gray-400 dark:text-gray-500"}`}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="8" cy="8" r="7" />
    <path d="M8 11v1" />
    <path d="M8 5.5c1.1 0 2 .9 2 2 0 .8-.5 1.5-1.2 1.8L8 9.8V10" />
  </svg>
);

export const sidebarConfig: SidebarConfig = {
  items: [
    {
      id: "main-section",
      type: SidebarItemType.SECTION,
      label: "",
      roles: [
        UserRole.ADMIN,
        UserRole.ORGANIZATION,
        UserRole.COMPANY,
        UserRole.USER,
      ],
      children: [
        {
          id: "home",
          type: SidebarItemType.LINK,
          label: "Home",
          path: "/",
          exact: true,
          icon: (active: boolean) => (
            <HomeIcon size={IconSize.SMALL} active={active} />
          ),
        },
        {
          id: "start-here",
          type: SidebarItemType.LINK,
          label: "Start Here",
          path: "/start-here",
          pathMatch: "start-here",
          icon: (active: boolean) => <StartHereIcon active={active} />,
        },
        {
          id: "spaces",
          type: SidebarItemType.LINK,
          label: "Spaces",
          path: "/spaces",
          pathMatch: "spaces",
          icon: (active: boolean) => <CirclesIcon active={active} />,
        },
        {
          id: "resources",
          type: SidebarItemType.LINK,
          label: "Resources",
          path: "/resources",
          pathMatch: "resources",
          icon: (active: boolean) => <CheckListIcon active={active} />,
        },
        {
          id: "calendar",
          type: SidebarItemType.LINK,
          label: "Events",
          path: "/calendar",
          pathMatch: "calendar",
          icon: (active: boolean) => <CalendarIcon active={active} />,
        },
        {
          id: "messages",
          type: SidebarItemType.LINK,
          label: "Chat",
          path: "/messages",
          pathMatch: "messages",
          icon: (active: boolean) => (
            <ChatIcon className={active ? "text-blue-500" : "text-gray-400 dark:text-gray-500"} />
          ),
        },
        /* {
          id: "tasks",
          type: SidebarItemType.LINK,
          label: "Tasks",
          path: "/tasks",
          pathMatch: "tasks",
          icon: (active: boolean) => <TasksIcon active={active} />,
        }, */
        /* {
          id: "faqs",
          type: SidebarItemType.LINK,
          label: "FAQs",
          path: "/faqs",
          exact: true,
          icon: (active: boolean) => <QuestionIcon active={active} />,
        }, */
      ],
    },
    {
      id: "community-section",
      type: SidebarItemType.SECTION,
      label: "Community",
      roles: [
        UserRole.ADMIN,
        UserRole.ORGANIZATION,
        UserRole.COMPANY,
        UserRole.USER,
      ],
      children: [
        {
          id: "community-members",
          type: SidebarItemType.LINK,
          label: "Members",
          path: "/community/members",
          pathMatch: "community/members",
          icon: (active: boolean) => <MembersIcon active={active} />,
        },
        {
          id: "community-connections",
          type: SidebarItemType.LINK,
          label: "Connections",
          path: "/community/connections",
          pathMatch: "community/connections",
          icon: (active: boolean) => <ConnectionsIcon active={active} />,
        },
        {
          id: "community-organizations",
          type: SidebarItemType.LINK,
          label: "Organizations",
          path: "/community/organizations",
          pathMatch: "community/organizations",
          icon: (active: boolean) => (
            <OrganizationCommunityIcon active={active} />
          ),
        },
      ],
    },
    {
      id: "admin-section",
      type: SidebarItemType.SECTION,
      label: "Admin",
      roles: [UserRole.ADMIN],
      children: [
        {
          id: "support-dashboard",
          type: SidebarItemType.LINK,
          label: "Support Dashboard",
          path: "/support",
          pathMatch: "support",
          icon: (active: boolean) => <SupportIcon active={active} />,
        },
      ],
    },
  ],
};
